import React from "react";
import { TooltipStyle } from "../../../../uiModel/tooltip/TooltipStyle";

import "@styles/c-tooltip.scss";

interface Props {
  x: number;
  y: number;
  text: string;
  licenceAdditionalText?: string;
  textLicence?: string;
  linkLicence?: string;
  isDesktop?: boolean;
  style: TooltipStyle;
  pointerEvents?: "none" | "auto";
}

interface State {}

interface TooltipLocation {
  x: number;
  y: number;
}
/**
 * In order to correctly position the tooltip we have to first render it and measure its dimensions
 * (the tooltip cannot extend beyond the margins).
 * The rendering proceeds as follows:
 * 1. Component receives props
 * 2. If props x, y coordinates are far away from previous props x, y coordinates tooltip is rendered with visibility 'hidden' -
 *    in this step the tooltip is only rendered so that we can measure it
 * 3. If props x, y coordinates are close to previous props x, y coordinates tooltip is rendered with visibility 'visible' -
 *    previously rendered tooltip is used to position currently rendered tooltip.
 *
 * 4. It would be best to render the tooltip with visibility 'hidden' every time the props change
 *    and position the tooltip in the subsequent step.
 *    Unfortunetely this doesn't look smooth in Safari (ie. the tooltip displaying current time above seekbar looks jagged)
 */
export class TooltipComponent extends React.Component<Props, State> {
  private static CLOSE_LOCATION_DISTANCE_THRESHOLD = 30;

  private tooltipLayer: HTMLElement;
  private tooltip: HTMLElement;
  private tooltipLocation: TooltipLocation;

  render() {
    const { x, y, text, licenceAdditionalText, textLicence, linkLicence, style, isDesktop, pointerEvents } = this.props;

    if (!text) {
      return <></>;
    }

    const newLocation = { x, y };
    const isLocationClose = this.areLocationsClose(this.tooltipLocation, newLocation);
    this.tooltipLocation = newLocation;

    const leftTopCoords = this.calculateLeftTopCoords();

    return (
      <div ref={(element) => (this.tooltipLayer = element)} className="c-tooltip" style={{ pointerEvents }}>
        <div
          style={{
            flexBasis: `${leftTopCoords.y}px`,
          }}
        />
        <div className="c-tooltip__verticalContainer">
          <div
            className="c-tooltip__tooltipLeftSpacer"
            style={{
              flexBasis: `${leftTopCoords.x}px`,
            }}
          />
          <div className="c-tooltip__tooltipHorizontalContainer">
            <div
              className={`c-tooltip__tooltipContent
                c-tooltip__tooltipContent--${style}
                ${isDesktop ? "c-tooltip__tooltipContent--desktop" : ""}`}
              ref={(element) => (this.tooltip = element)}
              style={{
                visibility: isLocationClose ? "visible" : "hidden",
              }}
            >
              {text}
              <div
                className="c-tooltip__tooltipContent__space"
                style={{
                  display: licenceAdditionalText ? "block" : "none",
                }}
              />
              {licenceAdditionalText}{" "}
              <a href={linkLicence} target="_blank">
                {textLicence}
              </a>
            </div>
          </div>
          <div className="c-tooltip__tooltipRightSpacer" />
        </div>
      </div>
    );
  }

  private areLocationsClose(l1: TooltipLocation, l2: TooltipLocation) {
    if (!l1 || !l2) {
      return false;
    }
    return (
      Math.abs(l1.x - l2.x) < TooltipComponent.CLOSE_LOCATION_DISTANCE_THRESHOLD &&
      Math.abs(l1.y - l2.y) < TooltipComponent.CLOSE_LOCATION_DISTANCE_THRESHOLD
    );
  }

  private calculateLeftTopCoords() {
    if (!this.tooltip) {
      return {
        x: 0,
        y: 0,
      };
    }

    const { x, y } = this.props;
    const layerBoundingBox = this.tooltipLayer.getBoundingClientRect();
    const tooltipBoundingBox = this.tooltip.getBoundingClientRect();

    const shiftedX = x - layerBoundingBox.left - tooltipBoundingBox.width / 2;
    const shiftedY = y - layerBoundingBox.top - tooltipBoundingBox.height;

    return {
      x: shiftedX > 0 ? Math.round(shiftedX) : 0,
      y: shiftedY > 0 ? Math.round(shiftedY) : 0,
    };
  }
}
